import { useTranslation } from "@familyzone/component-library";
import React, { FC, useState, useEffect } from "react";
import PageWithHeader from "../../templates/PageWithHeader";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { AggregatePills } from "./AggregatePills";
import { isLegacyLdapActive, SyncPill } from "./SyncPill";
import { useDeviceOptionsStore } from "../../../storez/DeviceOptionsStore";
import PropTypes from "prop-types";
import { listSyncConfigurations, SyncConfigurationNode, SyncType } from "../../../utils/api/SyncApi";

dayjs.extend(relativeTime);

/** The sync types the dashboard shows a pill for. */
const DASHBOARD_SYNC_TYPES: SyncType[] = [SyncType.Google, SyncType.Azure, SyncType.Ldap, SyncType.Ldaps];

const ConfigurationDashboard: FC = (_, context) => {
  const { t } = useTranslation();

  // Every Google / Entra / LDAP sync configuration on the device, from SM-API `/api/syncs`.
  const [syncs, setSyncs] = useState<SyncConfigurationNode[]>([]);

  const device = useDeviceOptionsStore((state) => state.options);

  const breadcrumbs = [
    { title: t("Configuration"), url: "/config/device/dashboard", isActive: false },
    { title: t("Dashboard"), isActive: true },
  ];
  const title = t("Dashboard");

  useEffect(() => {
    // The previous device's syncs must not survive a device switch.
    setSyncs([]);
    if (!device) return;

    // Check if this is a child device (has parent_id and is not a parent)
    const isChildDevice = !!device?.parent_id && !device?.is_parent;

    // Only fetch sync configs for parent devices
    if (isChildDevice) return;

    // A slow response for an earlier device must not overwrite the current one's.
    let cancelled = false;
    listSyncConfigurations(DASHBOARD_SYNC_TYPES)
      .then((conn) => {
        if (!cancelled) setSyncs((conn.edges ?? []).map((edge) => edge.node));
      })
      .catch((error) => {
        if (!cancelled) console.error("Failed to load sync configurations:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [device]);

  const ofType = (syncType: SyncType) => syncs.filter((s) => s.syncType === syncType);
  const googleSyncs = ofType(SyncType.Google);
  const azureSyncs = ofType(SyncType.Azure);
  const legacyLdapSyncs = ofType(SyncType.Ldap);
  const ldapServerSyncs = ofType(SyncType.Ldaps);

  const googleEnabled = googleSyncs.some((s) => s.enabled);
  const azureEnabled = azureSyncs.some((s) => s.enabled);
  // The legacy LDAP configuration only syncs users when its sync flag is on too. When it
  // does, it takes precedence over LDAP servers everywhere: pill status, message and link.
  const ldapEnabled = legacyLdapSyncs.some(isLegacyLdapActive);
  const ldap2Enabled = ldapServerSyncs.some((s) => s.enabled);

  // seems we're stuck on react-router v3
  /* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call*/

  const gridChildren: React.ReactNode[] = [<AggregatePills key="aggregate" />];

  if (googleEnabled) {
    gridChildren.push(
      <SyncPill
        key="google"
        syncType="GOOGLE"
        deviceInfo={device || null}
        syncs={googleSyncs}
        redirect={() => {
          context.router.push("/config/device/authentication/google");
        }}
      />
    );
  }

  if (ldapEnabled || ldap2Enabled) {
    gridChildren.push(
      <SyncPill
        key="ldap"
        syncType="LDAPS"
        deviceInfo={device || null}
        syncs={[...legacyLdapSyncs, ...ldapServerSyncs]}
        redirect={() => {
          context.router.push(ldapEnabled ? "/config/device/authentication/ldap" : "/config/device/authentication/servers");
        }}
      />
    );
  }

  if (azureEnabled) {
    gridChildren.push(
      <SyncPill
        key="azure"
        syncType="AZURE"
        deviceInfo={device || null}
        syncs={azureSyncs}
        redirect={() => {
          context.router.push("/config/device/authentication/azure");
        }}
      />
    );
  }

  return (
    <PageWithHeader title={title} breadcrumbs={breadcrumbs}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "24px", padding: "24px" }}>{gridChildren}</div>
    </PageWithHeader>
  );
};

ConfigurationDashboard.contextTypes = {
  router: PropTypes.object.isRequired,
};

export default ConfigurationDashboard;
